import React, {
    FC,
    useEffect,
    useState,
} from "react"
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Row,
} from "react-bootstrap"
import axios from "axios"
import BootstrapTable from "react-bootstrap-table-next"
import filterFactory, {
    textFilter,
} from "react-bootstrap-table2-filter"
import {useNavigate, useParams} from "react-router-dom"

import {useAuth} from "../../context/AuthProvider"
import {useTranslation} from "react-i18next"
import {ManagedExam} from "./participant.types"
import {
    ExamAccessReason,
    ExamAccessUser,
} from "./examManagement.types"

type ExamAccessViewProps = Record<string, unknown>

const getReasonLabel = (
    reason: ExamAccessReason,
    t: (key: string) => string
): string => {
    if (reason.type === "ADMIN") {
        return t("access.reasons.admin")
    }

    if (reason.type === "EXAM_OFFICE_EMPLOYEE") {
        return t("access.reasons.examOffice")
    }

    if (reason.type === "EXAM_OFFICE_ASSISTANT") {
        return t("access.reasons.examOfficeAssistant")
    }

    if (reason.type === "INFRASTRUCTURE_ACCESS") {
        return t("access.reasons.infrastructure")
    }

    if (reason.type === "DEAN_ACCESS") {
        return t("access.reasons.dean")
    }

    if (reason.role === "facultyOwner") {
        return t("access.reasons.facultyOwner")
    }

    if (reason.role === "deputy") {
        return t("access.reasons.deputy")
    }

    return t("access.reasons.read")
}

const getTenantLabel = (
    reason: ExamAccessReason
): string => {
    if (!reason.tenant) {
        return ""
    }

    return reason.tenant.fullName?.de ??
        reason.tenant.name?.de ??
        reason.tenant.describer
}

const ExamAccessView: FC<ExamAccessViewProps> = () => {
    const auth = useAuth()
    const nav = useNavigate()
    const params = useParams()
    const {t, i18n} = useTranslation("examManagement")

    const examId = params.examId

    const [exam, setExam] = useState<ManagedExam | null>(null)
    const [users, setUsers] = useState<ExamAccessUser[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [loadError, setLoadError] = useState<boolean>(false)

    useEffect(() => {
        if (!examId) {
            setLoading(false)
            setLoadError(true)
            return
        }

        Promise.all([
            axios.get(
                `/api/exam/exam/${examId}`,
                auth.axiosAuthHeader()
            ),
            axios.get(
                `/api/exam/exam/${examId}/access`,
                auth.axiosAuthHeader()
            ),
        ])
            .then(([examResponse, accessResponse]) => {
                setExam(examResponse.data)
                setUsers(
                    Array.isArray(accessResponse.data)
                        ? accessResponse.data
                        : []
                )
                setLoadError(false)
            })
            .catch(error => {
                console.error(
                    "[Exam access view] Failed to load access data",
                    error
                )

                setUsers([])
                setLoadError(true)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [examId])

    const caretDefinition = (
        order: string | undefined
    ) => {
        if (!order) {
            return <i className="bi bi-arrow-down-up ms-1"/>
        }

        return order === "asc"
            ? <i className="bi bi-sort-up ms-1"/>
            : <i className="bi bi-sort-down ms-1"/>
    }

    const columns = [
        {
            dataField: "given_name",
            text: t("access.columns.givenName"),
            sort: true,
            filter: textFilter({
                placeholder: " ",
            }),
            formatter: (cell: string | undefined) => cell || "–",
            sortCaret: caretDefinition,
        },
        {
            dataField: "family_name",
            text: t("access.columns.familyName"),
            sort: true,
            filter: textFilter({
                placeholder: " ",
            }),
            formatter: (cell: string | undefined) => cell || "–",
            sortCaret: caretDefinition,
        },
        {
            dataField: "email",
            text: "E-Mail",
            sort: true,
            filter: textFilter({
                placeholder: " ",
            }),
            formatter: (cell: string | undefined) => cell || "–",
            sortCaret: caretDefinition,
        },
        {
            dataField: "reasons",
            text: t("access.columns.reason"),
            formatter: (
                reasons: ExamAccessReason[] | undefined
            ) => {
                return (
                    <div className="d-flex flex-wrap gap-1">
                        {(reasons ?? []).map((reason, index) => (
                            <Badge
                                key={`${reason.type}-${reason.role ?? ""}-${reason.tenant?._id ?? ""}-${index}`}
                                bg="secondary"
                            >
                                {getReasonLabel(reason, t)}
                                {getTenantLabel(reason)
                                    ? `: ${getTenantLabel(reason)}`
                                    : ""}
                            </Badge>
                        ))}
                    </div>
                )
            },
            filterValue: (
                reasons: ExamAccessReason[] | undefined
            ) => {
                return (reasons ?? [])
                    .map(reason => [
                        getReasonLabel(reason, t),
                        getTenantLabel(reason),
                    ].join(" "))
                    .join(" ")
            },
            filter: textFilter({
                placeholder: " ",
            }),
            sort: false,
        },
    ]

    return (
        <div className="component">
            <Container fluid>
                <Row>
                    <Col>
                        <Card>
                            <Card.Header>
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                    <div>
                                        <h3 className="mb-0">
                                            <i className="bi bi-shield-lock me-2"/>
                                            {t("access.title")}
                                        </h3>

                                        {exam && (
                                            <div className="text-muted mt-1">
                                                {i18n.language === "en" ? exam.name_en || exam.name_de : exam.name_de}
                                            </div>
                                        )}
                                    </div>

                                    <Button
                                        variant="secondary"
                                        onClick={() => nav("/exam-management")}
                                    >
                                        <i className="bi bi-arrow-left me-1"/>
                                        {t("backToOverview")}
                                    </Button>
                                </div>
                            </Card.Header>

                            <Card.Body>
                                {loading && (
                                    <Alert className="text-center" variant="info">
                                        {t("access.loading")}
                                    </Alert>
                                )}

                                {!loading && loadError && (
                                    <Alert className="text-center" variant="danger">
                                        {t("access.loadError")}
                                    </Alert>
                                )}

                                {!loading && !loadError && users.length === 0 && (
                                    <Alert className="text-center" variant="info">
                                        {t("access.empty")}
                                    </Alert>
                                )}

                                {!loading && !loadError && users.length > 0 && (
                                    <BootstrapTable
                                        keyField="_id"
                                        data={users}
                                        columns={columns}
                                        filter={filterFactory()}
                                        striped
                                        hover
                                        condensed
                                        defaultSorted={[
                                            {
                                                dataField: "family_name",
                                                order: "asc",
                                            },
                                        ]}
                                        noDataIndication={() => (
                                            <Alert className="text-center" variant="info">
                                                {t("noResults")}
                                            </Alert>
                                        )}
                                    />
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default ExamAccessView
